import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import useGridFilter, { DefaultGridFilter } from "../../../system/hooks/useGridFilter";
import { GlobalImportInvoicesDataProvider } from "../../../Services/DataProviders/GlobalImportInvoicesDataProvider";
import { IImportedInvoiceItemDTO, normalizeImportedInvoiceItem } from "../../../libs/coreapi-dto/documents/importedInvoiceItem";

import { LoadingStatus } from "../../../@types/enumsGlobal";
import { mapGridRowToLocalFile } from "./mapGridRowToLocalFile";
import { formatNumberWithTwoDecimalPlaces } from "../../../components/controls/inputs/BaseInput";
import localStyles from "./ImportedInvoiceItemGrid.module.scss";
import { ImportedInvoiceContext } from "./importedInvoiceContext";

interface IImportedInvoiceItemGridProps {
    gridId: string;
    plugin: IPluginSettings;
}

const ImportedInvoiceItemGrid: FC<IImportedInvoiceItemGridProps> = (props) => {
    const appContext = useAppContext();
    const pluginCtx = usePluginContext();

    const dataProvider = new GlobalImportInvoicesDataProvider(appContext.coreApiService);

    const [allData, setAllData] = useState<IImportedInvoiceItemDTO[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();

    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultGridFilter);
    const { t } = useTranslation();
    const invoice = useContext(ImportedInvoiceContext);

    const updateData = (items: IImportedInvoiceItemDTO[]) => {
        setAllData(items);
        setTotalCount(items.length);
        setIsSubmitting(false)
    };

    useEffect(() => {
        if (invoice) {
            const items = (invoice.items ?? []).map(normalizeImportedInvoiceItem);
            updateData(items);
            return;
        }

        const selectedRow = pluginCtx.masterGrid.selectedItem;
        if (!selectedRow) {
            setAllData([]);
            setTotalCount(0);
            setIsSubmitting(false)
            return;
        }

        const file = mapGridRowToLocalFile(selectedRow);
        if (!file) {
            setAllData([]);
            setTotalCount(0);
            setIsSubmitting(false)
            return;
        }

        const storeId = file.idStoreGlobal || "";
        const contractorId = file.idContractorGlobal || "";
        if (!storeId || !contractorId) {
            setAllData([]);
            setTotalCount(0);
            setIsSubmitting(false)
            return;
        }

        setIsSubmitting(true);
        dataProvider.loadAndGetByFileName({ file, store: storeId }, (entity) => {
            const items = (entity?.items ?? []).map(normalizeImportedInvoiceItem);
            updateData(items);
        });
    }, [invoice, pluginCtx.masterGrid.selectedItem]);

    const paginatedData = useMemo(() => {
        let sortedData = [...allData];
        //сортировка
        const sortProperty = gridFilter?.fieldOrderBy;
        const orderDesc = gridFilter?.orderDesc;
        
        if (sortProperty) {
            sortedData.sort((a, b) => {
                const aValue = a[sortProperty as keyof IImportedInvoiceItemDTO];
                const bValue = b[sortProperty as keyof IImportedInvoiceItemDTO];
                
                // Обработка null/undefined значений
                if (aValue == null && bValue == null) return 0;
                if (aValue == null) return 1;
                if (bValue == null) return -1;

                let result = 0;

                // Сравнение строк
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    result = aValue.localeCompare(bValue);
                }
                // Сравнение чисел
                else if (typeof aValue === 'number' && typeof bValue === 'number') {
                    result = aValue - bValue;
                }
                // Сравнение булевых значений
                else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
                    result = aValue === bValue ? 0 : aValue ? 1 : -1;
                }
                // Общее сравнение
                else {
                    if (aValue < bValue) result = -1;
                    else if (aValue > bValue) result = 1;
                    else result = 0;
                }

                // Если orderDesc === true, сортируем по убыванию
                return orderDesc ? -result : result;
            });
        }

        // пагинация
        const pageNumber = gridFilter?.pageNumber || 1;
        const numberPerPage = gridFilter?.numberPerPage || 15;
        
        const startIndex = (pageNumber - 1) * numberPerPage;
        const endIndex = Math.min(startIndex + numberPerPage, sortedData.length);
        
        return sortedData.slice(startIndex, endIndex);
    }, [allData, gridFilter?.fieldOrderBy, gridFilter?.orderDesc, gridFilter?.pageNumber, gridFilter?.numberPerPage]);

    useEffect(() => {
        setTotalCount(allData.length);
    }, [allData]);

    const handlePageNumberChange = (pageNumber: number) => {
        dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber } });
    };

    const handleNumberPerPageChange = (numberPerPage: number) => {
        dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage } });
    };

    const handleSort = (propertyName: string) => {
        const currentFieldOrderBy = gridFilter?.fieldOrderBy;
        const currentOrderDesc = gridFilter?.orderDesc ?? true;
        
        let newOrderDesc = true;
        if (currentFieldOrderBy === propertyName) {
            newOrderDesc = !currentOrderDesc;
        }
        
        dispatchGridFilter({ 
            type: "sort", 
            payload: propertyName,
        });
    };

    const handleSelect = (row?: IGridRow) => {
        setSelectedItem(row);
    };

    const selectedFile = pluginCtx.masterGrid.selectedItem as any;

    const headerTotals = useMemo(() => ({
        count: selectedFile?.itemCount ?? null,
        sumIncVat: selectedFile?.sumIncVat ?? null
    }), [selectedFile]);

    const rowTotals = useMemo(() => allData.reduce(
        (acc, item) => ({
            count: acc.count + 1,
            sumIncVat: acc.sumIncVat + (item.sumIncVat ?? 0)
        }),
        { count: 0, sumIncVat: 0 }
    ), [allData]);

    const totals = useMemo(() => ({
        count: headerTotals.count ?? rowTotals.count,
        sumIncVat: headerTotals.sumIncVat ?? rowTotals.sumIncVat
    }), [headerTotals, rowTotals]);

    if (isSubmitting) {
        return <Spinner />;
    }

    return (
        <PluginWrapper>
            <DefaultGrid
                gridId={props.gridId}
                data={paginatedData}
                totalCount={totalCount}
                filter={gridFilter}
                plugin={props.plugin}
                localData
                singleDirectory
                searching
                hiddenPagination={{ hiddenCountRow: true, hiddenNumberPage: true }}
                selectedItem={selectedItem}
                onSelect={handleSelect}
                onSort={(i) => handleSort(i.propertyName)}
                onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                onPageNumberChange={handlePageNumberChange}
                onNumberPerPageChange={handleNumberPerPageChange}
            />
            <div className={localStyles.footer}>
                <div className={localStyles.footerItem}>
                    <span className={localStyles.footerLabel}>{t("importInvoice.globalImport.footer.quantity")}:</span>
                    <span className={localStyles.footerValue}>{totals.count}</span>
                </div>
                <div className={localStyles.footerItem}>
                    <span className={localStyles.footerLabel}>{t("importInvoice.globalImport.footer.sumIncVat")}:</span>
                    <span className={localStyles.footerValue}>{formatNumberWithTwoDecimalPlaces(totals.sumIncVat)}</span>
                </div>
            </div>
        </PluginWrapper>
    );
};

export default ImportedInvoiceItemGrid;
